import React, { useState, useEffect } from 'react';
import { Modal, Button, Card, CardBody, CardHeader, Row, Col, Badge } from 'react-bootstrap';
import QRCode from 'react-qr-code';
import { API_BASE_URL } from '../config/api';
import { QrCode, Copy, Link, Mail, Binary, ScanQrCode } from 'lucide-react';

const QRCodeDisplay = ({ show, onHide, session, conference }) => {
  const [qrUrl, setQrUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [maskedCode, setMaskedCode] = useState('');

  useEffect(() => {
    if (show && session) {
      // Generate the URL for the QR code using masked attendance code instead of session ID
      const baseUrl = window.location.origin;
      // Create a simple hash of the attendance code for URL masking
      const code = btoa(session.attendanceCode).replace(/[+/=]/g, '').substring(0, 8);
      setMaskedCode(code);
      const attendanceUrl = `${baseUrl}/attendance/${code}`;
      setQrUrl(attendanceUrl);
    }
  }, [show, session]);

  if (!session || !conference) return null;

  const formatTime = (timeString) => {
    if (!timeString) return '';
    
    try {
      const [hours, minutes] = timeString.split(':');
      const hour = parseInt(hours);
      const minute = parseInt(minutes);
      
      const period = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      const displayMinute = minute.toString().padStart(2, '0');
      
      return `${displayHour}:${displayMinute} ${period}`;
    } catch (error) {
      console.error('Error formatting time:', error);
      return timeString;
    }
  };

  const renderConferenceType = (type) => {
           const typeLabels = {
         'mm': 'M&M Conference',
         'ground_rounds': 'GR Conference',
         'both': 'M&M & GR Conference',
         'educational': 'Education Conference',
         'journal_club': 'Journal Club'
       };
    
    const typeColors = {
      'mm': 'primary',
      'ground_rounds': 'success',
      'both': 'warning',
      'educational': 'info',
      'journal_club': 'secondary'
    };
    
    return {
      label: typeLabels[type] || type,
      color: typeColors[type] || 'secondary'
    };
  };

  const handleEmailShare = () => {
    const baseUrl = window.location.origin;
    const qrPageUrl = maskedCode ? `${baseUrl}/qr/${maskedCode}` : '';
    const subject = `Conference Attendance QR Code - ${conference.title}`;
    const body = `Hello,

Please use the following QR code or URL to record your attendance for the conference session:

Conference: ${conference.title}
Date: ${new Date(session.date + 'T00:00:00').toLocaleDateString('en-US', { 
  weekday: 'long', 
  year: 'numeric', 
  month: 'long', 
  day: 'numeric' 
})}
${conference.startTime && conference.endTime ? `Time: ${formatTime(conference.startTime)} - ${formatTime(conference.endTime)}` : ''}
${session.location ? `Location: ${session.location}` : ''}
Attendance Code: ${session.attendanceCode}

Attendance Page URL: ${qrUrl}
${qrPageUrl ? `QR Code Display Page: ${qrPageUrl}` : ''}

You can either:
1. Scan the QR code with your phone camera
2. Click the attendance URL above to access the attendance page
3. Click the QR code display page to view the QR code and conference details
4. Share these URLs with others who need to record attendance

Best regards`;

    const mailtoLink = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailtoLink, '_blank');
  };

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(qrUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = qrUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const qrCodeContent = (
      <Row>
        {/* QR Code Card */}
        <Col md={6} className="mb-3 mb-md-0">
          <Card className="border-0 rounded-0 border-end p-0 h-100">
              <CardHeader className="border-0 px-3 py-2 d-flex align-items-center justify-content-start gap-3">
                <span className="bg-primary d-flex align-items-center justify-content-center rounded-3 p-2" style={{ width: '40px', height: '40px' }}>
                 <QrCode/>
              </span>
              <h6 className="fw-bold mb-0">QR Code</h6>
            </CardHeader>
            <CardBody className="p-0 d-flex flex-column align-items-center justify-content-center">
              <QRCode
                className="rounded-4 p-4 mb-3"
                style={{
                  border: '3px solid var(--gray-200)',
                  background: '#fff'
                }}
                value={qrUrl}
                level="H"
                includeMargin={true}
              />
              <Button variant="outline-primary" size="sm" className="mb-2" onClick={() => window.open(`/qr/${maskedCode}`, '_blank')}>
                <div className="d-flex align-items-center justify-content-start gap-2">
                  <Copy/>
                  <span>Open QR Code Page</span>
                </div>
              </Button>
              <p className="text-muted small mb-0">Point your camera at the QR code to check in</p>
            </CardBody>
          </Card> 
        </Col>
        {/* URL and Attendance Code Cards */}
        <Col md={6} className="d-flex flex-column align-items-center">
          {/* URL Card */}
          <Card className=" border-0 rounded-0 w-100 h-100">
            <CardHeader className="card-header border-0 px-4 py-3 pt-4 d-flex align-items-center justify-content-start gap-3">
              <span className="bg-secondary rounded-3 d-flex align-items-center justify-content-center p-2" style={{ width: '40px', height: '40px' }}>
                <Link/>
              </span>
              <h6 className="fw-bold mb-0">QR Code URL</h6>
            </CardHeader>
            <CardBody className="px-4 py-0">
              <div className="d-flex align-items-center bg-primary border p-2 justify-content-between rounded mb-2 p-2 w-100">
                <code className="d-block align-items-center text-truncate bg-primary">
                  <mark className="bg-primary p-0">{qrUrl}</mark>
                </code>
                <Button
                  variant="light"
                  size="sm"
                  onClick={handleCopyUrl}
                  className="py-1 px-2"
                  title="Copy URL"
                >
                  {copied ? <i className="bi bi-check text-success"></i> : <i className="bi bi-clipboard"></i>}
                </Button>
              </div>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleEmailShare}
                className="w-100 mb-2"
              >
                <Mail/>
                Share via Email
              </Button>
              <p className="text-muted small mb-4">
                Share this URL directly or scan the QR code
              </p>
            </CardBody>
          </Card>
          <hr className="my-3 w-100" />
          {/* Attendance Code Card */}
          <Card className="border-0 rounded-0 w-100 h-100">
            <CardHeader className="card-header border-0 px-4 py-3 pt-4 d-flex align-items-center justify-content-start gap-3">
              <span className="bg-success rounded-3 d-flex align-items-center justify-content-center p-2" style={{ width: '40px', height: '40px' }}>
                <Binary/>
              </span>
              <h6 className="fw-bold mb-0">Attendance Code</h6>
            </CardHeader>
            <CardBody className="px-4 py-0">
              <div className="d-flex align-items-center flex-column justify-content-center position-relative rounded-3 mb-2 w-100"
                style={{
                  background:
                    "linear-gradient(45deg, var(--blue) 0%, var(--green) 70%, var(--cyan) 100%)",
                }}>
                <code className="d-flex align-items-center h1 fw-bold text-white py-2 m-0" style={{ backgroundColor: 'transparent' }}>
                  {session.attendanceCode}
                </code>
                <div className="w-100 px-0 rounded-bottom-2 absolute bottom-0 left-0 right-0 d-flex  align-items-center justify-content-center"
                  style={{
                    background: "linear-gradient(45deg, transparent 0%, var(--card-bg) 100%)",
                    height: "10px",
                  }}
                ></div>
              </div>
              <p className="text-muted small mb-3">
                Enter this code to check in
              </p>
            </CardBody>
          </Card>
        </Col>
      </Row>
    
  );

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton className="border-0 p-md-4">
        <Modal.Title className="d-flex align-items-center justify-content-start gap-3">
          <ScanQrCode />
          Attendance QR Code
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-4">
        {qrCodeContent}
      </Modal.Body>
      <Modal.Footer className="border-0 pt-0">
        <Button variant="light" onClick={onHide} size="sm"
                style={{ borderRadius: '8px' }}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default QRCodeDisplay; 